/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Lifecycle Studio overview", description = "Lifecycle Studio is a campaign planning and content production workspace for lifecycle marketing — create briefs, plan and generate messages, and send them to your ESP.", lastUpdatedDate = new Date(1792368000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Lifecycle Studio overview",
        description: "Lifecycle Studio is a campaign planning and content production workspace for lifecycle marketing — create briefs, plan and generate messages, and send them to your ESP.",
        lastUpdatedDate: "Mon Oct 19 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How it works end to end",
          "depth": 1,
          "id": "how-it-works-end-to-end"
        }, {
          "value": "Access Lifecycle Studio",
          "depth": 0,
          "id": "access-lifecycle-studio"
        }, {
          "value": "Core features",
          "depth": 0,
          "id": "core-features"
        }, {
          "value": "Creation modes",
          "depth": 1,
          "id": "creation-modes"
        }, {
          "value": "Templates",
          "depth": 1,
          "id": "templates"
        }, {
          "value": "Images",
          "depth": 1,
          "id": "images"
        }, {
          "value": "Use Lifecycle Studio with other Hightouch products",
          "depth": 0,
          "id": "use-lifecycle-studio-with-other-hightouch-products"
        }, {
          "value": "Lifecycle Studio vs. Ad Studio",
          "depth": 1,
          "id": "lifecycle-studio-vs-ad-studio"
        }, {
          "value": "Use cases",
          "depth": 0,
          "id": "use-cases"
        }, {
          "value": "Get started",
          "depth": 0,
          "id": "get-started"
        }, {
          "value": "For platform admins",
          "depth": 1,
          "id": "for-platform-admins"
        }, {
          "value": "For marketers and creative teams",
          "depth": 1,
          "id": "for-marketers-and-creative-teams"
        }, {
          "value": "Permissions",
          "depth": 0,
          "id": "permissions"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      img: "img",
      h3: "h3",
      a: "a",
      ol: "ol",
      li: "li",
      code: "code",
      h4: "h4",
      ul: "ul"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Marketers, creative teams, platform admins"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Lifecycle Studio is a workspace for planning and producing lifecycle marketing campaigns. Describe a campaign goal, and the agent drafts a brief, plans the messages, and generates channel-specific content you refine, approve, and export to your connected ESP."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Lifecycle Studio is a campaign planning and content production workspace for lifecycle marketing. It covers the path from campaign idea to deliverable message: you provide a goal or brief, the agent plans the messages and generates candidates, you refine the content, and then export approved content to your connected email service provider (ESP) or marketing platform."
      }), "\n", _jsx(_components.p, {
        children: "You can start from a rough goal and use the agent to draft a campaign plan, or bring a fully formed brief and move straight to content production."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/lifecycle-studio/campaigns/ls-campaigns-overview.png",
          alt: "Lifecycle Studio Campaigns list view"
        })
      }), "\n", _jsx(_components.h3, {
        id: "how-it-works-end-to-end",
        children: "How it works end to end"
      }), "\n", _jsxs(_components.p, {
        children: ["Before your first campaign, set up your workspace once: add business context, brand guidelines, and guardrails in ", _jsx(_components.a, {
          href: "/context-hub/overview",
          children: "Context Hub"
        }), " and configure a default parent model in ", _jsx(_components.a, {
          href: "/agents/settings",
          children: "Agents settings"
        }), ". This gives the agent the brand, audience, and channel context it uses when generating content. Connecting an ESP is optional — it unlocks export and template import workflows but is not required to draft and review content."]
      }), "\n", _jsx(_components.p, {
        children: "A campaign then moves through three stages:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Plan"
          }), " — The agent drafts a brief and a message plan, a checklist of the messages it intends to create. You review and refine both, or skip review and generate immediately."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create"
          }), " — The agent generates candidate messages for each item in the plan. You review, edit, and save the ones you want."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Launch"
          }), " — You pair each saved candidate with an audience and export it to your connected ESP for delivery."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Lifecycle Studio shares agent infrastructure with other Hightouch products, including a shared context layer for business context, brand guidelines, and guardrails. See ", _jsx(_components.a, {
          href: "/context-hub/overview",
          children: "Context Hub"
        }), " for the shared workspace configuration."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "access-lifecycle-studio",
        children: "Access Lifecycle Studio"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.a, {
            href: "https://app.hightouch.com/lifecycle-studio?from=docs",
            children: _jsx(_components.strong, {
              children: "app.hightouch.com/lifecycle-studio"
            })
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Campaigns"
          }), " or ", _jsx(_components.strong, {
            children: "Messages"
          }), " to begin."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "core-features",
        children: "Core features"
      }), "\n", _jsx(_components.p, {
        children: "Lifecycle Studio has two creation modes and two supporting asset libraries."
      }), "\n", _jsx(_components.h3, {
        id: "creation-modes",
        children: "Creation modes"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Mode"
            }), _jsx(_components.th, {
              children: "When to use it"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.a, {
                  href: "/lifecycle-studio/messages",
                  children: "Messages"
                })
              })
            }), _jsx(_components.td, {
              children: "You're launching a one-off send, like a promo or an announcement. Start from a template, prompt the agent, and iterate until it's production-ready."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: _jsx(_components.a, {
                  href: "/lifecycle-studio/campaigns",
                  children: "Campaigns"
                })
              })
            }), _jsx(_components.td, {
              children: "You're running an ongoing lifecycle program, like a welcome series or a win-back. The agent plans a coordinated set of messages that you refine and extend over time."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Messages"
        }), " are the fastest path from idea to a finished email. You select a template, describe what you need, and iterate with the agent in a chat-style editor. You refine each message in the editor — visually or in HTML — and approve it before export. When a campaign generates multiple candidates, each one appears as a separate message card you can open and refine individually. See ", _jsx(_components.a, {
          href: "/lifecycle-studio/messages",
          children: "Messages"
        }), " for more detail."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Campaigns"
        }), " are where you run a lifecycle program over time. You describe a goal, like \"re-engage lapsed customers\" or \"launch a seasonal promotion,\" and the agent drafts a brief, plans the messages it will create, and generates candidates you refine and send. Because a campaign holds a whole program rather than a single message, you can keep adding to it as the program grows. See ", _jsx(_components.a, {
          href: "/lifecycle-studio/campaigns",
          children: "Campaigns"
        }), " for more detail."]
      }), "\n", _jsx(_components.h3, {
        id: "templates",
        children: "Templates"
      }), "\n", _jsxs(_components.p, {
        children: ["Templates are the structural starting points the agent uses when generating messages. Import them from raw HTML or an ", _jsx(_components.code, {
          children: ".eml"
        }), " file, from a connected ESP, or from Figma, and the agent builds new content on top of them. The agent selects templates based on the campaign context and message content. You can also save any finished message back to the template library. See ", _jsx(_components.a, {
          href: "/lifecycle-studio/templates",
          children: "Templates"
        }), " for more detail."]
      }), "\n", _jsx(_components.h3, {
        id: "images",
        children: "Images"
      }), "\n", _jsxs(_components.p, {
        children: ["The image library stores the visual assets the agent can use in generated messages. You can upload images, edit them in place, tag them for better agent selection, and browse edit history. You can also connect external asset sources to import images automatically. See ", _jsx(_components.a, {
          href: "/lifecycle-studio/images",
          children: "Images"
        }), " for more detail."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-lifecycle-studio-with-other-hightouch-products",
        children: "Use Lifecycle Studio with other Hightouch products"
      }), "\n", _jsx(_components.p, {
        children: "Lifecycle Studio uses context, audiences, and creative assets from your Hightouch workspace to generate messages, then exports the approved ones to your connected ESP."
      }), "\n", _jsx(_components.h4, {
        id: "context-audiences-and-assets-the-agent-uses",
        children: "Context, audiences, and assets the agent uses"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Business context, brand guidelines, and guardrails"
          }), " — Configured in ", _jsx(_components.a, {
            href: "/context-hub/overview",
            children: "Context Hub"
          }), " and applied to every campaign the agent runs. The agent uses this context when generating briefs, plans, and messages."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Customer Studio audiences"
          }), " — Used for targeting when you export. You select which audiences receive which candidates."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Campaign history"
          }), " — When you connect an ESP, Lifecycle Studio can import your existing campaign history for context."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Templates and images"
          }), " — Your existing email templates, HTML, and brand imagery provide the structural and visual foundation for generated content."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "messages-you-export-audiences-you-sync",
        children: "Messages you export, audiences you sync"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Messages to your ESP"
          }), " — Approved messages are exported to your ", _jsx(_components.a, {
            href: "/context-hub/connections",
            children: "connected ESP"
          }), ", where they enter the platform's campaign or journey workflow."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Audiences to destinations"
          }), " — When you export, selected audiences are synced to your delivery platform. Candidates are exported separately."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "lifecycle-studio-vs-ad-studio",
        children: "Lifecycle Studio vs. Ad Studio"
      }), "\n", _jsxs(_components.p, {
        children: ["Both products generate marketing content, but they serve different channels. Lifecycle Studio focuses on lifecycle campaigns (email, push, and SMS) delivered through an ESP. ", _jsx(_components.a, {
          href: "/ad-studio/overview",
          children: "Ad Studio"
        }), " focuses on paid media creative (display and social ads) published to ad platforms. The two products share the same agent infrastructure and context layer but have distinct workflows."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "use-cases",
        children: "Use cases"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Use case"
            }), _jsx(_components.th, {
              children: "Example"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Welcome series"
              })
            }), _jsx(_components.td, {
              children: "Create a three-email welcome campaign for new signups, with each message personalized to the user's signup source and first action."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Re-engagement"
              })
            }), _jsx(_components.td, {
              children: "Generate a lapsed-customer campaign with messages targeting different inactivity windows (30, 60, 90 days) and varied messaging tones."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Promotional campaign"
              })
            }), _jsx(_components.td, {
              children: "Describe a seasonal sale, and the agent generates email variants with different offers, subject lines, and CTAs for A/B testing."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Post-purchase nurture"
              })
            }), _jsx(_components.td, {
              children: "Generate a follow-up sequence for recent buyers that cross-sells related products using catalog data."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Campaign refresh"
              })
            }), _jsx(_components.td, {
              children: "Take an existing high-performing email, generate new variants with updated copy and imagery, and test them against the original."
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "get-started",
        children: "Get started"
      }), "\n", _jsx(_components.p, {
        children: "You can start creating messages in Lifecycle Studio right away. Connecting an ESP unlocks export and delivery workflows, and adding workspace context improves the quality of generated content."
      }), "\n", _jsx(_components.h3, {
        id: "for-platform-admins",
        children: "For platform admins"
      }), "\n", _jsx(_components.p, {
        children: "These steps are typically done once during initial workspace setup. None are required to start creating messages, but each unlocks additional capabilities."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "What to do"
            }), _jsx(_components.th, {
              children: "What it unlocks"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/business-context#add-business-context",
                children: "Add business context"
              })
            }), _jsx(_components.td, {
              children: "Tells the agent about your brand, products, audience, and lifecycle goals"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/guardrails#set-email-guardrails",
                children: "Set brand guidelines and guardrails"
              })
            }), _jsx(_components.td, {
              children: "Controls the tonal and visual rules the agent follows, and what it should avoid"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/agents/settings",
                children: "Configure the parent model"
              })
            }), _jsx(_components.td, {
              children: "Connects your Customer Studio audience data for use when sending"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/context-hub/connections",
                children: "Connect your ESP"
              })
            }), _jsx(_components.td, {
              children: "Enables exporting messages to your ESP for delivery and importing existing campaign history"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "for-marketers-and-creative-teams",
        children: "For marketers and creative teams"
      }), "\n", _jsx(_components.p, {
        children: "Once the workspace is set up, seed the creative inputs the agent uses, then start creating content. You can refine these over time as you learn what works."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "What to do"
            }), _jsx(_components.th, {
              children: "Why"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/lifecycle-studio/templates",
                children: "Upload email templates"
              })
            }), _jsx(_components.td, {
              children: "Gives the agent structural starting points for message generation"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/lifecycle-studio/images",
                children: "Upload images"
              })
            }), _jsx(_components.td, {
              children: "Lets the agent incorporate your brand imagery into generated messages"
            })]
          })]
        })]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Upload a range of template styles, like promotional, transactional, and editorial, to give the agent more options when generating messages for different campaign types."
        })
      }), "\n", _jsx(_components.p, {
        children: "Once you have templates uploaded, pick a creation mode based on what you need:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["For a quick single email, use ", _jsx(_components.a, {
            href: "/lifecycle-studio/messages",
            children: "Messages"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["For structured end-to-end production, create a ", _jsx(_components.a, {
            href: "/lifecycle-studio/campaigns",
            children: "campaign"
          }), ": fill out the intake modal with your goal and audience, review the brief and message plan the agent drafts in the ", _jsx(_components.strong, {
            children: "Plan"
          }), " tab, generate and refine candidates in ", _jsx(_components.strong, {
            children: "Create"
          }), ", then pair them with audiences and export in ", _jsx(_components.strong, {
            children: "Launch"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Most teams start with Messages, then move to Campaigns once they're comfortable reviewing and refining generated content."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "permissions",
        children: "Permissions"
      }), "\n", _jsx(_components.p, {
        children: "Lifecycle Studio must be enabled for your workspace by Hightouch. Once enabled, access is controlled through custom roles:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "Where to configure"
            }), _jsx(_components.th, {
              children: "Required for"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/workspace-management/roles#agents",
                children: _jsx(_components.strong, {
                  children: "Access Agents"
                })
              })
            }), _jsx(_components.td, {
              children: "Agents tab"
            }), _jsx(_components.td, {
              children: "Viewing Lifecycle Studio in the app"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/workspace-management/roles#agents",
                children: _jsx(_components.strong, {
                  children: "Configure Agents"
                })
              })
            }), _jsx(_components.td, {
              children: "Agents tab"
            }), _jsx(_components.td, {
              children: "Modifying Agents settings: context, brand, guardrails, channels, image tags, and integrations"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/workspace-management/roles#parent-models",
                children: _jsx(_components.strong, {
                  children: "View parent model data"
                })
              })
            }), _jsx(_components.td, {
              children: "Customer Studio tab"
            }), _jsx(_components.td, {
              children: "Viewing row-level audience member data in send flows"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/workspace-management/roles#parent-models",
                children: _jsx(_components.strong, {
                  children: "Configure audiences & syncs"
                })
              })
            }), _jsx(_components.td, {
              children: "Customer Studio tab"
            }), _jsx(_components.td, {
              children: "Creating, editing, and saving audiences used in send flows"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Beyond these role grants, the agent's access to your customer data follows the same governance as the rest of Hightouch. Column-level privacy and row-level access rules apply to the data the agent can query and use for personalization. See ", _jsx(_components.a, {
          href: "/agents/overview#data-governance",
          children: "Agents data governance"
        }), " for how these layers work."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Lifecycle Studio never sends a message without your approval. You always review and approve generated content before it is exported to your ESP."
        })
      })]
    });
  }
}
export default MDXContent;
